import React from 'react';
import { ShoppingItem } from '../../../lib/schemas';
import { ShoppingBag, Tag, ChevronRight, Zap } from 'lucide-react';
import { useCartAssistant } from "../../../lib/cartAssistant/CartAssistantProvider";
import { liveState, useStore } from '../../../lib/store';
import { useTranslation } from 'react-i18next';
import { CURRENCIES } from '../../../../constants';
import { motion } from 'framer-motion';
import { getGlobalLatticePrices } from '../../../../services/supabaseService';
import { useSupabase } from '../../../lib/SupabaseContext';
import { GhostPulse } from '../../../../components/common/GhostPulse';
import { SmartImage } from '../../../../components/common/SmartImage';
import { UnitPriceIconButton, UnitPriceCheckDetail, applyLooksRight } from '../../../../components/common/UnitPriceCheckPanel';

interface CurrentBasketDisplayProps {
  items: ShoppingItem[];
  activeCurrencySymbol?: string;
}

export const CurrentBasketDisplay: React.FC<{ props: CurrentBasketDisplayProps; isStreaming?: boolean }> = ({ 
  props,
  isStreaming
}) => {
  const { t } = useTranslation();
  const { userProfile, currency, items: storeItems, updateItem } = useStore();
  const { items = [], activeCurrencySymbol: propCurrencySymbol } = props || {};
  // Row whose shelf unit-price detail is open.
  const [openCheckId, setOpenCheckId] = React.useState<string | null>(null);

  const activeCurrencySymbol = React.useMemo(() => {
    return CURRENCIES.find(c => c.code === (currency || userProfile?.settings?.interface?.currency))?.symbol || propCurrencySymbol || '$';
  }, [userProfile?.settings?.interface?.currency, propCurrencySymbol]);
  
  // Use live store to find actual image URLs if AI truncated them
  const liveItems = liveState.items;
  
  // Safety check for items
  // PIVOT: If AI sent empty items (desync), use live state as fallback
  const basketItems = (Array.isArray(items) && items.length > 0) ? items : liveItems;
  
  const total = basketItems.reduce((sum, item) => {
    // Handle both objects and strings
    if (typeof item === 'string') {
      // Handle various currency symbols in the string
      const priceMatch = (item as any).match(/[$\€£¥](\d+\.?\d*)/);
      return sum + (priceMatch ? parseFloat(priceMatch[1]) : 0);
    }
    const price = typeof item?.price === 'number' ? item.price : 0;
    const quantity = typeof item?.quantity === 'number' ? item.quantity : 1;
    return sum + (price * quantity);
  }, 0);

  if (basketItems.length === 0 && !isStreaming) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center">
        <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-300">
          <ShoppingBag size={24} />
        </div>
        <p className="text-xs font-black text-[#1a2b3c] uppercase tracking-widest">{t('basket.basket_empty_chat')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="bg-[#1a2b3c] p-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-emerald-500 rounded-lg text-white">
            <ShoppingBag size={14} />
          </div>
          <span className="text-[10px] font-black text-white uppercase tracking-widest">{t('basket.active_basket')}</span>
        </div>
        <span className="text-xs font-black text-emerald-400">
          {activeCurrencySymbol}{total.toFixed(2)}
        </span>
      </div>
      
      <div className="max-h-[200px] overflow-y-auto divide-y divide-gray-50">
        {basketItems.map((item, idx) => {
          // Normalize item data
          const isString = typeof item === 'string';
          const name = isString ? item : (item?.name || t('common.unknown_item'));
          const price = isString ? 0 : (typeof item?.price === 'number' ? item.price : 0);
          const quantity = isString ? 1 : (typeof item?.quantity === 'number' ? item.quantity : 1);
          const itemId = isString ? `str-${idx}` : (item?.id || `idx-${idx}`);
          
          let imageUrl = isString ? undefined : item?.imageUrl;
          
          // RECOVERY LOGIC: Try to find the real image from live memory
          if (item) {
            // Case A: AI sent an object but image is truncated/missing
            if (!isString && item.id && (!imageUrl || imageUrl === '(base64_image_data)' || imageUrl.length < 5)) {
              const match = liveItems.find((li: any) => li.id === item.id);
              if (match?.imageUrl) imageUrl = match.imageUrl;
            } 
            // Case B: AI sent a string, try to match by name
            else if (isString) {
              const cleanName = (item as any).split(' — ')[0].split(' (')[0].trim();
              const match = liveItems.find((li: any) => li.name.toLowerCase().includes(cleanName.toLowerCase()));
              if (match?.imageUrl) imageUrl = match.imageUrl;
            }
          }

          // The shelf unit-price check lives on the basket item, not in the AI's props.
          const live = isString ? undefined : (storeItems.find(li => li.id === item.id) || storeItems.find(li => li.name === name));

          return (
            <div key={itemId}>
            <div className="p-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                {imageUrl ? (
                  <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0 border border-gray-100">
                    <SmartImage imageUrl={imageUrl} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-500 shrink-0">
                    <Tag size={14} />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center">
                    <p className="text-[11px] font-bold text-[#1a2b3c] truncate leading-tight">{name}</p>
                    {!isString && item.canonicalId && (
                      <GhostPulse 
                        itemId={itemId}
                        slug={item.canonicalId} 
                        currentPrice={price} 
                      />
                    )}
                  </div>
                  {!isString && (
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">
                      {quantity}x {activeCurrencySymbol}{price.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
              {!isString && (
                <div className="flex items-center gap-1 shrink-0">
                  {live && (
                    <UnitPriceIconButton item={live} isExpanded={openCheckId === itemId} onToggle={() => setOpenCheckId(openCheckId === itemId ? null : itemId)} />
                  )}
                  <p className="text-[10px] font-black text-[#1a2b3c] text-right">
                    {activeCurrencySymbol}{(price * quantity).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
            {live && (
              <div className="px-3">
                <UnitPriceCheckDetail item={live} isExpanded={openCheckId === itemId} currencySymbol={activeCurrencySymbol} onLooksRight={() => { applyLooksRight(live, updateItem); setOpenCheckId(null); }} />
              </div>
            )}
            </div>
          );
        })}
      </div>
      
      <div className="p-3 bg-gray-50/50 border-t border-gray-100 flex justify-between items-center">
        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{t('basket.items_count', { count: basketItems.length })}</span>
        <div className="flex items-center gap-1 text-[9px] font-black text-emerald-600 uppercase tracking-widest">
          {t('basket.view_in_app')} <ChevronRight size={10} />
        </div>
      </div>
    </div>
  );
};
